import React, { useCallback, useMemo, useState } from 'react';
import { useDrag } from 'react-dnd';
import {
  ArrowRightIcon,
  MapPinIcon,
  ArrowUpRightIcon,
  ArrowDownLeftIcon,
  CaretDownIcon,
  DotsSixVerticalIcon,
  ShippingContainerIcon,
} from '@phosphor-icons/react';
import * as Popover from '@radix-ui/react-popover';
import * as Dialog from '@radix-ui/react-dialog';
import { OrderStatus } from 'src/types/enums';

import classNames from '../../../../utils/classnames';
import { formatDateShort, formatInputTime } from '../../../../utils/date';
import { Pill, type PillColor } from '../../../../components/pill/Pill';
import { Input } from '../../../../components/input/Input';
import { Button } from '../../../../components/button/Button';
import { parseIntOrNull } from '../../../../utils/number';
import { ORDER_STATUS_COLOR, ORDER_STATUS_OPTIONS } from '../../../order/constants';
import type { PlanningBoardTruckOrder, PlanningBoardBacklogOrder } from '../../models';
import type { DragItem } from '../PlanningBoardPage';
import { getRegionFromBusinessUnit, type Region } from '../../endpoints/shared.types';
import { getTrailerTypeLabel, formatCurrency, getLoadUnloadInfo, getOrderedStops } from '../../utils';

// ============================================================================
// Types
// ============================================================================

interface OrderCardProps {
  order: PlanningBoardTruckOrder | PlanningBoardBacklogOrder;
  sourceTruckId: string | null;
  /** Backlog variant (left rail). The default variant is the load inside a truck card. */
  compact?: boolean;
  onStatusChange?: (orderId: string, status: string) => void;
  onKmChange?: (orderId: string, manualFullKm: number | null, manualEmptyKm: number | null) => Promise<void>;
}

// ============================================================================
// Helpers
// ============================================================================

const REGION_DOT: Record<Region, string> = {
  all: 'bg-brand',
  benelux: 'bg-bu-benelux',
  france: 'bg-bu-france',
  germany: 'bg-bu-germany',
  forwarding: 'bg-bu-forwarding',
};

const REGION_LABEL: Record<Region, string> = {
  all: 'BNL',
  benelux: 'BNL',
  france: 'FR',
  germany: 'DE',
  forwarding: 'FWD',
};

function getStatusLabel(status: string): string {
  return ORDER_STATUS_OPTIONS.find((o) => o.key === status)?.name ?? status;
}

function getStatusColor(status: string): PillColor {
  return ORDER_STATUS_COLOR[status as OrderStatus] ?? 'neutral';
}

const STATUS_DOT: Record<PillColor, string> = {
  positive: 'bg-positive',
  negative: 'bg-negative',
  info: 'bg-info',
  warning: 'bg-warning',
  primary: 'bg-warning',
  neutral: 'bg-fg-muted',
  default: 'bg-fg-muted',
  muted: 'bg-fg-muted',
};

/** Statuses a planner can set from the board. */
const SELECTABLE_STATUSES: OrderStatus[] = [
  OrderStatus.Created,
  OrderStatus.Placed,
  OrderStatus.Confirmed,
  OrderStatus.Executed,
  OrderStatus.Refused,
  OrderStatus.Cancelled,
];

/** Small outlined chip used for trailer type, business unit, direction. */
const MetaChip: React.FC<{ children: React.ReactNode; title?: string; className?: string }> = ({
  children,
  title,
  className,
}) => (
  <span
    title={title}
    className={classNames(
      'inline-flex h-[19px] shrink-0 items-center gap-1 rounded-sm border border-border px-1.5 text-2xs font-semibold text-fg-secondary',
      className,
    )}
  >
    {children}
  </span>
);

// ============================================================================
// Component
// ============================================================================

export const OrderCard: React.FC<OrderCardProps> = ({
  order,
  sourceTruckId,
  compact = false,
  onStatusChange,
  onKmChange,
}) => {
  // Set up drag source
  const [{ isDragging }, drag] = useDrag<DragItem, void, { isDragging: boolean }>(
    () => ({
      type: 'order',
      item: {
        type: 'order',
        order,
        sourceTruckId,
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [order, sourceTruckId],
  );

  // Memoize expensive stop computation to avoid recalc on every render
  const orderedStops = useMemo(() => getOrderedStops(order), [order]);
  const { firstLoad, lastUnload } = useMemo(() => getLoadUnloadInfo(order), [order]);
  const totalDistance = useMemo(() => order.lines.reduce((sum, line) => sum + line.distance, 0) / 1000, [order.lines]);
  const trailerType = getTrailerTypeLabel(order.allowedTrailerTypes[0] ?? null);
  const autoEmptyKm = 'autoEmptyKm' in order ? order.autoEmptyKm : null;
  const orderNumber = 'orderNumber' in order ? order.orderNumber : null;
  const customerName = 'customer' in order ? order.customer?.name : null;

  const [fullKmDraft, setFullKmDraft] = useState(
    order.manualFullKm != null ? String(order.manualFullKm) : String(Math.round(totalDistance)),
  );
  const [emptyKmDraft, setEmptyKmDraft] = useState(
    order.manualEmptyKm != null ? String(order.manualEmptyKm) : autoEmptyKm != null ? String(autoEmptyKm) : '',
  );
  const [isSavingKm, setIsSavingKm] = useState(false);
  const [isKmDialogOpen, setIsKmDialogOpen] = useState(false);

  const fullKmValue = parseIntOrNull(fullKmDraft);
  const emptyKmValue = parseIntOrNull(emptyKmDraft);
  const manualTotalKm = (fullKmValue ?? 0) + (emptyKmValue ?? 0);

  const handleKmDialogOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setFullKmDraft(order.manualFullKm != null ? String(order.manualFullKm) : String(Math.round(totalDistance)));
        setEmptyKmDraft(
          order.manualEmptyKm != null ? String(order.manualEmptyKm) : autoEmptyKm != null ? String(autoEmptyKm) : '',
        );
      }
      setIsKmDialogOpen(open);
    },
    [order.manualFullKm, order.manualEmptyKm, totalDistance, autoEmptyKm],
  );

  const handleKmSave = useCallback(async () => {
    if (!onKmChange) return;
    setIsSavingKm(true);
    try {
      await onKmChange(order.id, fullKmValue, emptyKmValue);
      setIsKmDialogOpen(false);
    } finally {
      setIsSavingKm(false);
    }
  }, [onKmChange, order.id, fullKmValue, emptyKmValue]);

  const displayFullKm = order.manualFullKm ?? Math.round(totalDistance);
  const displayEmptyKm = order.manualEmptyKm ?? autoEmptyKm ?? 0;
  const displayTotalKm = displayFullKm + displayEmptyKm;
  const isEmptyKmAuto = order.manualEmptyKm == null && autoEmptyKm != null;

  // Determine the region based on the order's business unit
  const businessUnit = 'businessUnit' in order ? order.businessUnit : 0;
  const orderRegion = getRegionFromBusinessUnit(businessUnit);

  // Determine if export (outbound) or import (inbound) based on origin/destination
  // Export = destination is outside Benelux, Import = destination is Benelux
  const isExport = orderRegion !== 'benelux';

  const statusColor = getStatusColor(order.status);
  const statusLabel = getStatusLabel(order.status);

  // ── shared rows ─────────────────────────────────────────────────────────

  const orderNumberLink = orderNumber ? (
    <a
      href={`/internal/orders/${order.id}/general`}
      target="_blank"
      rel="noopener noreferrer"
      className="shrink-0 font-semibold text-fg hover:text-brand-text hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      {orderNumber}
    </a>
  ) : null;

  const routeRow = (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
      {orderedStops.length > 0 ? (
        orderedStops.map((stop, idx) => (
          <React.Fragment key={stop.id}>
            {idx > 0 && <ArrowRightIcon className="h-3 w-3 shrink-0 text-fg-muted" weight="bold" />}
            <span className="inline-flex min-w-0 items-center gap-1">
              <MapPinIcon
                className={classNames('h-3 w-3 shrink-0', stop.type === 'Load' ? 'text-brand' : 'text-fg-muted')}
                weight="fill"
              />
              <span className="truncate text-xs font-semibold text-fg">{stop.location.city}</span>
              <span className="text-2xs text-fg-muted">{stop.location.postalCode}</span>
            </span>
          </React.Fragment>
        ))
      ) : (
        <span className="text-xs text-fg-muted">Geen stops</span>
      )}
    </div>
  );

  const hasTimes = firstLoad?.timeStart != null || lastUnload?.timeStart != null;
  const timesRow = hasTimes ? (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2xs text-fg-secondary">
      {firstLoad?.timeStart != null && (
        <span className="whitespace-nowrap">
          <span className="font-semibold text-brand-text">Laad</span> {formatDateShort(firstLoad.date)}{' '}
          {formatInputTime(firstLoad.timeStart)}
          {firstLoad.timeEnd != null && firstLoad.timeEnd !== firstLoad.timeStart
            ? `–${formatInputTime(firstLoad.timeEnd)}`
            : ''}
        </span>
      )}
      {lastUnload?.timeStart != null && (
        <span className="whitespace-nowrap">
          <span className="font-semibold text-fg-muted">Los</span> {formatDateShort(lastUnload.date)}{' '}
          {formatInputTime(lastUnload.timeStart)}
          {lastUnload.timeEnd != null && lastUnload.timeEnd !== lastUnload.timeStart
            ? `–${formatInputTime(lastUnload.timeEnd)}`
            : ''}
        </span>
      )}
    </div>
  ) : null;

  const regionChip = (
    <MetaChip title="Business unit">
      <span className={classNames('h-1.5 w-1.5 rounded-full', REGION_DOT[orderRegion])} />
      {REGION_LABEL[orderRegion]}
    </MetaChip>
  );

  const trailerChip = trailerType ? (
    <MetaChip title={`Trailertype: ${order.allowedTrailerTypes.join(', ')}`}>
      <ShippingContainerIcon className="h-3 w-3 text-fg-muted" />
      {trailerType}
    </MetaChip>
  ) : null;

  const directionLabel = isExport ? 'Export' : 'Import';
  // Backlog cards have no km line, so the direction chip carries the distance there.
  const directionChip = (
    <MetaChip title={directionLabel} className="font-medium">
      {isExport ? (
        <ArrowUpRightIcon className="h-3 w-3 text-info" weight="bold" />
      ) : (
        <ArrowDownLeftIcon className="h-3 w-3 text-positive" weight="bold" />
      )}
      {compact ? `${Math.round(totalDistance)} km` : directionLabel}
    </MetaChip>
  );

  // ── compact: backlog card ──────────────────────────────────────────────

  if (compact) {
    return (
      <div
        ref={(node) => {
          drag(node);
        }}
        className={classNames(
          'flex cursor-grab flex-col gap-1.5 rounded-lg border border-border bg-surface p-3 transition-colors hover:border-border-strong',
          isDragging && 'opacity-50',
        )}
      >
        <div className="flex items-center gap-2">
          <span
            className={classNames('h-[7px] w-[7px] shrink-0 rounded-full', REGION_DOT[orderRegion])}
            title={REGION_LABEL[orderRegion]}
          />
          <span className="flex min-w-0 flex-1 items-baseline gap-1.5 text-sm">
            {orderNumberLink}
            {orderNumber && order.customerRef && <span className="text-border-strong">·</span>}
            {order.customerRef && (
              <span className="truncate text-2xs text-fg-muted" title={order.customerRef}>
                {order.customerRef}
              </span>
            )}
          </span>
          {order.status === OrderStatus.Cancelled && (
            <Pill color="negative" size="sm">
              {statusLabel}
            </Pill>
          )}
          <DotsSixVerticalIcon className="h-3.5 w-3.5 shrink-0 text-fg-muted" weight="bold" />
        </div>

        {customerName && <div className="truncate text-xs text-fg-secondary">{customerName}</div>}

        {routeRow}
        {timesRow}

        <div className="flex flex-wrap items-center gap-1.5">
          {regionChip}
          {trailerChip}
          {directionChip}
          <span className="ml-auto text-xs font-semibold text-fg">€{formatCurrency(order.saleTotal)}</span>
        </div>
      </div>
    );
  }

  // ── default: load inside a truck card ──────────────────────────────────

  const kmText = (
    <>
      {displayFullKm} km vol / {displayEmptyKm}
      {isEmptyKmAuto && (
        <sup className="ml-px text-[9px]" title="Automatisch berekend">
          auto
        </sup>
      )}{' '}
      km leeg / {displayTotalKm} km totaal
    </>
  );

  return (
    <div
      ref={(node) => {
        drag(node);
      }}
      className={classNames(
        'flex cursor-grab flex-col gap-2 rounded-md border border-border bg-surface p-3 transition-colors hover:border-border-strong',
        isDragging && 'opacity-50',
      )}
    >
      {/* Order number + customer ref */}
      {(orderNumber || order.customerRef) && (
        <div className="flex min-w-0 items-baseline gap-1.5 text-xs">
          {orderNumberLink}
          {orderNumber && order.customerRef && <span className="text-border-strong">·</span>}
          {order.customerRef && (
            <span className="truncate text-2xs text-fg-muted" title={order.customerRef}>
              {order.customerRef}
            </span>
          )}
        </div>
      )}

      {routeRow}
      {timesRow}

      {/* Meta: BU, trailer type, direction */}
      <div className="flex flex-wrap items-center gap-1.5">
        {regionChip}
        {trailerChip}
        {directionChip}
      </div>

      {/* Kilometres — click to override */}
      <div className="flex items-center text-2xs text-fg-muted">
        {onKmChange ? (
          <Dialog.Root open={isKmDialogOpen} onOpenChange={handleKmDialogOpenChange}>
            <Dialog.Trigger asChild>
              <button
                type="button"
                className="text-left hover:text-brand-text hover:underline"
                title="Kilometers aanpassen"
                onClick={(e) => e.stopPropagation()}
              >
                {kmText}
              </button>
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay className="dialog-overlay" />
              <Dialog.Content className="dialog-content max-w-md" onClick={(e) => e.stopPropagation()}>
                <Dialog.Title className="heading-one mb-4">Kilometers aanpassen</Dialog.Title>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void handleKmSave();
                  }}
                >
                  <div className="mb-3 grid grid-cols-2 gap-3">
                    <div>
                      <label className="label-text mb-1.5 block">Volle km</label>
                      <Input
                        type="number"
                        step="1"
                        value={fullKmDraft}
                        onChange={setFullKmDraft}
                        isDisabled={isSavingKm}
                      />
                    </div>
                    <div>
                      <label className="label-text mb-1.5 block">Lege km</label>
                      <Input
                        type="number"
                        step="1"
                        value={emptyKmDraft}
                        onChange={setEmptyKmDraft}
                        isDisabled={isSavingKm}
                        placeholder={autoEmptyKm != null ? String(autoEmptyKm) : undefined}
                      />
                    </div>
                  </div>
                  <div className="mb-4 text-xs text-fg-muted">
                    Totaal: <span className="font-medium text-fg-secondary">{manualTotalKm} km</span>
                  </div>
                  <div className="flex justify-between">
                    <Button
                      color="secondary"
                      isDisabled={isSavingKm}
                      onTrigger={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsKmDialogOpen(false);
                      }}
                    >
                      Annuleer
                    </Button>
                    <Button type="submit" color="primary" isLoading={isSavingKm} isDisabled={isSavingKm}>
                      Opslaan
                    </Button>
                  </div>
                </form>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        ) : (
          <span>{kmText}</span>
        )}
      </div>

      {/* Status + prices */}
      <div className="-mx-3 -mb-3 mt-0.5 flex items-center gap-2 rounded-b-md border-t border-border bg-surface-sunken px-3 py-2">
        {onStatusChange ? (
          <Popover.Root>
            <Popover.Trigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-full"
                title="Status wijzigen"
                onClick={(e) => e.stopPropagation()}
              >
                <Pill color={statusColor} size="sm">
                  {statusLabel}
                </Pill>
                <CaretDownIcon className="h-3 w-3 text-fg-muted" weight="bold" />
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                align="start"
                sideOffset={4}
                className="z-dropdown min-w-40 rounded-md border border-border bg-surface p-1 shadow-md"
              >
                {SELECTABLE_STATUSES.map((status) => {
                  const isCurrent = order.status === status;
                  return (
                    <Popover.Close asChild key={status}>
                      <button
                        type="button"
                        className={classNames(
                          'flex h-8 w-full items-center gap-2 rounded px-2 text-left text-xs transition-colors hover:bg-surface-hover',
                          isCurrent ? 'font-semibold text-fg' : 'text-fg-secondary',
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isCurrent) {
                            onStatusChange(order.id, status);
                          }
                        }}
                      >
                        <span
                          className={classNames('h-2 w-2 shrink-0 rounded-full', STATUS_DOT[getStatusColor(status)])}
                        />
                        {getStatusLabel(status)}
                      </button>
                    </Popover.Close>
                  );
                })}
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        ) : (
          <Pill color={statusColor} size="sm">
            {statusLabel}
          </Pill>
        )}

        <span className="ml-auto flex items-baseline gap-1 whitespace-nowrap text-xs">
          {'purchaseTotal' in order && order.purchaseTotal > 0 && (
            <>
              <span className="font-medium text-fg-secondary" title="Aankoop">
                €{formatCurrency(order.purchaseTotal)}
              </span>
              <span className="text-border-strong">/</span>
            </>
          )}
          <span className="font-semibold text-fg" title="Verkoop">
            €{formatCurrency(order.saleTotal)}
          </span>
        </span>
      </div>
    </div>
  );
};
